import type { Cliente, ItemPedido, Pedido, SubPedido } from '../db/tipos'
import { arredondar2 } from '../pedidos/precos'

// ▶ Central da Carteira (tela Clientes, decisoes dela em 08/10). Modulo PURO:
// a tela so desenha. Nada e gravado -- tudo sai dos pedidos, visitas e do
// proprio cliente, entao nao ha migration nem coluna nova.
//
// "Compra" = pedido FECHADO e vivo (F3), a MESMA regra de resumoDeCompras da
// Rota: se as duas discordassem, o card da Rota e o de Clientes mostrariam
// ultimas compras diferentes do mesmo cliente.

export type Compra = { data: string; valor: number; produtos: number }

export type SituacaoDeCompra = 'comprando' | 'em_risco' | 'esquecido' | 'sem_compra'

// F4: retorno de X em X dias vale X; rota semanal vale 15; sem nenhum dos dois, 30.
// `dia_visita !== null`, NUNCA teste de verdade: domingo e 0.
export function prazoDoCliente(cliente: Pick<Cliente, 'dia_visita'> & { retorno_dias?: number | null }): number {
  if (cliente.retorno_dias != null && cliente.retorno_dias > 0) return cliente.retorno_dias
  if (cliente.dia_visita !== null) return 15
  return 30
}

export function diasEntre(deISO: string, ateISO: string): number {
  const [a1, m1, d1] = deISO.slice(0, 10).split('-').map(Number)
  const [a2, m2, d2] = ateISO.slice(0, 10).split('-').map(Number)
  return Math.round((Date.UTC(a2, m2 - 1, d2) - Date.UTC(a1, m1 - 1, d1)) / 86_400_000)
}

// As compras de cada cliente, da MAIS NOVA para a mais velha.
// Com `representadaId`, so as empresas daquela fornecedora contam (a analise
// por fornecedora): o mesmo cliente sai "comprando" na A e "esquecido" na B sem
// existir duas vezes no cadastro.
export function comprasPorCliente(
  pedidos: readonly Pedido[],
  subPedidos: readonly SubPedido[],
  itens: readonly ItemPedido[],
  representadaId: string | null,
): Map<string, Compra[]> {
  const subsVivos = new Map<string, SubPedido[]>()
  for (const s of subPedidos) {
    if (s.apagado_em !== null || s.status === 'cancelado') continue
    if (representadaId !== null && s.representada_id !== representadaId) continue
    const lista = subsVivos.get(s.pedido_id)
    if (lista) lista.push(s)
    else subsVivos.set(s.pedido_id, [s])
  }
  const produtosDoSub = new Map<string, Set<string>>()
  for (const i of itens) {
    if (i.apagado_em !== null) continue
    const set = produtosDoSub.get(i.sub_pedido_id)
    if (set) set.add(i.produto_id)
    else produtosDoSub.set(i.sub_pedido_id, new Set([i.produto_id]))
  }

  const mapa = new Map<string, Compra[]>()
  for (const p of pedidos) {
    if (p.apagado_em !== null || p.status !== 'fechado') continue
    const subs = subsVivos.get(p.id) ?? []
    // Por fornecedora, pedido sem empresa viva dela nao e compra dela. Sem
    // fornecedora, segue a Rota: o pedido fechado conta mesmo com valor zero.
    if (representadaId !== null && subs.length === 0) continue
    const produtos = new Set<string>()
    for (const s of subs) for (const id of produtosDoSub.get(s.id) ?? []) produtos.add(id)
    const compra: Compra = {
      data: p.data,
      valor: arredondar2(subs.reduce((soma, s) => soma + s.valor_total, 0)),
      produtos: produtos.size,
    }
    const lista = mapa.get(p.cliente_id)
    if (lista) lista.push(compra)
    else mapa.set(p.cliente_id, [compra])
  }
  for (const lista of mapa.values()) lista.sort((a, b) => b.data.localeCompare(a.data))
  return mapa
}

// E2-1 (b): passou do prazo = Em risco; passou do DOBRO = Esquecido.
export function situacaoDeCompra(compras: readonly Compra[], prazo: number, hojeISO: string): SituacaoDeCompra {
  if (compras.length === 0) return 'sem_compra'
  const dias = diasEntre(compras[0].data, hojeISO)
  if (dias <= prazo) return 'comprando'
  if (dias <= prazo * 2) return 'em_risco'
  return 'esquecido'
}

// Q1-Q4: a ultima compra contra a MEDIA das anteriores; queda de 20% ou mais
// no valor OU no numero de produtos diferentes. Uma compra so nao tem com o
// que comparar.
const QUEDA = 0.8

export function comQueda(compras: readonly Compra[]): boolean {
  if (compras.length < 2) return false
  const [ultima, ...anteriores] = compras
  const mediaValor = anteriores.reduce((s, c) => s + c.valor, 0) / anteriores.length
  const mediaProdutos = anteriores.reduce((s, c) => s + c.produtos, 0) / anteriores.length
  const caiuValor = mediaValor > 0 && ultima.valor <= mediaValor * QUEDA
  const caiuProdutos = mediaProdutos > 0 && ultima.produtos <= mediaProdutos * QUEDA
  return caiuValor || caiuProdutos
}

// E2-3: o mesmo prazo da F4, contado da ultima VISITA. Nunca visitado conta.
export function semVisitaRecente(ultimaVisitaISO: string | null, prazo: number, hojeISO: string): boolean {
  if (ultimaVisitaISO === null) return true
  return diasEntre(ultimaVisitaISO, hojeISO) > prazo
}

export const ROTULO_SITUACAO: Record<SituacaoDeCompra, string> = {
  comprando: 'Comprando',
  em_risco: 'Em risco',
  esquecido: 'Esquecido',
  sem_compra: 'Sem compra',
}

// P1 (aprovada 08/10): Saude = (Comprando + metade dos Em risco) / quem ja
// comprou x 100. Esquecido vale 0. "Sem compra" fica FORA: os clientes que
// nunca compraram pelo app (127 de 152 na conta dela, medido) derrubariam a
// nota sem dizer nada sobre a carteira que compra.
export function saudeDaCarteira(conta: { comprando: number; em_risco: number; esquecido: number }): number | null {
  const base = conta.comprando + conta.em_risco + conta.esquecido
  if (base === 0) return null
  return Math.round(((conta.comprando + conta.em_risco / 2) / base) * 100)
}

export function fraseDaSaude(nota: number): string {
  if (nota >= 80) return 'Saúde ótima'
  if (nota >= 60) return 'Saúde boa, mas existem oportunidades de recuperação'
  if (nota >= 40) return 'Atenção: parte da carteira está esfriando'
  return 'Carteira precisa de cuidado'
}
